import React from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, Copy, X } from 'lucide-react';
import { ghostBtn, helperClass, labelClass } from '../components/GatewayUi';
import { llmInferenceBaseUrl, llmInferenceCurlSample } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';

const PLACEHOLDER_KEY = '$SELFIC_LLM_KEY';

export type LlmCallHelpTarget = {
  id: string;
  name: string;
  publicAlias: string;
  llmType?: string;
  routeKey: string;
  inferenceKeyLast4?: string | null;
  hasInferenceKey?: boolean;
};

function CopyBlock({
  label,
  value,
  isLight,
  onCopy,
}: {
  label: string;
  value: string;
  isLight: boolean;
  onCopy: (value: string, label: string) => void;
}) {
  return (
    <div>
      <p className={labelClass(isLight)}>{label}</p>
      <div className="flex gap-2 items-start">
        <pre
          className={`flex-1 min-w-0 rounded-xl border px-3 py-2.5 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto ${
            isLight ? 'border-neutral-200 bg-white text-neutral-800' : 'border-neutral-700 bg-neutral-900 text-neutral-200'
          }`}
        >
          {value}
        </pre>
        <button type="button" className={ghostBtn(isLight)} disabled={!value} onClick={() => onCopy(value, label)}>
          <Copy size={14} />
        </button>
      </div>
    </div>
  );
}

export default function LlmCallHelpDialog({
  target,
  isLight,
  onClose,
  onToast,
}: {
  target: LlmCallHelpTarget | null;
  isLight: boolean;
  onClose: () => void;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  if (!target) return null;

  const baseUrl = llmInferenceBaseUrl(target.routeKey);
  const kind = (target.llmType || '').toLowerCase().includes('embed') ? 'embedding' : 'chat';
  const sampleInput = { baseUrl, token: PLACEHOLDER_KEY, model: target.publicAlias, kind } as const;
  const bearerSample = llmInferenceCurlSample({ ...sampleInput, auth: 'bearer' });
  const apiKeySample = llmInferenceCurlSample({ ...sampleInput, auth: 'api_key' });

  const copy = async (text: string, label: string) => {
    if (!text) {
      onToast('error', `${label} is empty`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      onToast('success', `${label} copied`);
      logGovernedMcp('app', 'inference_call_help_copied', { label, quotaId: target.id });
    } catch {
      onToast('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'inference_call_help_copy_failed', { label, quotaId: target.id });
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="llm-call-help-title"
        className={`relative w-full max-w-xl rounded-2xl border shadow-2xl overflow-hidden ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
              }`}
            >
              <BookOpen size={18} />
            </div>
            <div className="min-w-0">
              <h3 id="llm-call-help-title" className={`text-base font-semibold tracking-tight ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                How to call
              </h3>
              <p className={`text-sm mt-1.5 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                Same key works as Authorization Bearer or Claude-style x-api-key. Replace {PLACEHOLDER_KEY} with the key shown when you issued it.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg shrink-0 ${isLight ? 'hover:bg-neutral-100 text-neutral-400' : 'hover:bg-neutral-800 text-neutral-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className={`rounded-xl border px-3.5 py-3 text-sm ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-900/70'}`}>
            <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{target.name}</p>
            <p className={`mt-1 text-xs font-mono break-all ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{baseUrl}</p>
            {target.hasInferenceKey && target.inferenceKeyLast4 ? (
              <p className={helperClass(isLight)}>Issued key last four {target.inferenceKeyLast4}</p>
            ) : (
              <p className={helperClass(isLight)}>Issue a key on this row, then paste it into the samples.</p>
            )}
          </div>
          <CopyBlock label="Authorization Bearer" value={bearerSample} isLight={isLight} onCopy={(value, label) => void copy(value, label)} />
          <CopyBlock label="x-api-key" value={apiKeySample} isLight={isLight} onCopy={(value, label) => void copy(value, label)} />
        </div>
      </div>
    </div>,
    document.body
  );
}
